import React from 'react';
import { useTranslation } from 'react-i18next';
import CheckIcon from 'components/atoms/icons/checkIcon';
import BaseButton from 'components/atoms/baseButton';
import PencilIcon from 'components/atoms/icons/pencilIcon';
import classNames from 'classnames';
import FadeAnimation from 'components/atoms/base-animations/fade';

import s from './styles.module.scss';

import type { ReactNode } from 'react';

interface IMedicationBlockProps {
  title: string;
  loading?: boolean;
  canEdit?: boolean;
  disabledSave?: boolean;
  disabled?: boolean;
  isEdit?: boolean;
  onEdit?: () => void;
  onSave?: () => void;
  onCancel?: () => void;
  additionalControls?: ReactNode;
  children?: ReactNode;
}

const MedicationBlock: React.FC<IMedicationBlockProps> = (props) => {
  const {
    title,
    canEdit = true,
    isEdit = false,
    disabledSave = false,
    disabled = false,
    loading = false,
    children,
    onEdit,
    onSave,
    onCancel,
    additionalControls,
  } = props;

  const { t } = useTranslation();

  const buttonsClassNames = classNames(s.buttons, {
    [s.disabled]: disabled,
  });

  return (
    <section className={s.card} aria-label={title}>
      <div className={s.header}>
        <h3 className={s['section-title']}>{title}</h3>
        {canEdit && (
          <FadeAnimation uniqueKey={`${isEdit}`}>
            <div className={buttonsClassNames}>
              {isEdit && (
                <>
                  {additionalControls}
                  <BaseButton
                    type="secondary"
                    label={t('controls.cancel')}
                    onClick={onCancel}
                    dataCy="cancel-medication-button"
                    disabled={loading}
                  />
                  {onSave && (
                    <BaseButton
                      type="success"
                      label={t('controls.save')}
                      onClick={onSave}
                      icon={<CheckIcon />}
                      loading={loading}
                      disabled={disabledSave}
                      dataCy="save-medication-button"
                    />
                  )}
                </>
              )}
              {!isEdit && !disabled && (
                <BaseButton
                  type="secondary"
                  onClick={onEdit}
                  icon={<PencilIcon />}
                  loading={loading}
                  dataCy={`edit-medication-button-${title}`}
                />
              )}
            </div>
          </FadeAnimation>
        )}
      </div>
      <div className={s.body}>{children}</div>
    </section>
  );
};

export default MedicationBlock;
